import type * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Alert from '@cloudscape-design/components/alert';
import Badge from '@cloudscape-design/components/badge';
import BarChart from '@cloudscape-design/components/bar-chart';
import Box from '@cloudscape-design/components/box';
import Container from '@cloudscape-design/components/container';
import ContentLayout from '@cloudscape-design/components/content-layout';
import DatePicker from '@cloudscape-design/components/date-picker';
import Header from '@cloudscape-design/components/header';
import Multiselect, { type MultiselectProps } from '@cloudscape-design/components/multiselect';
import Select, { type SelectProps } from '@cloudscape-design/components/select';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Table from '@cloudscape-design/components/table';
import type {
  ComputeCashflowResult,
  Currency,
  FinanceAccount,
  FinanceCashflowBucket,
  FinanceCashflowGranularity,
} from '@etiquetachef/types';
import { ALL_FINANCE_CASHFLOW_GRANULARITIES } from '@etiquetachef/types';
import { subscribeFinanceAccounts } from '@/lib/firestoreFinance';
import { computeCashflowCallable, listFinanceTagsCallable } from '@/lib/callables';
import { formatCurrencyCents } from '@/features/dashboard-finance/formatters';
import { SignedCents, TagBreakdownTable } from '@/features/finance-analysis/financeAnalysisShared';
import { dayNoonUtcMs, msToDateInput } from '@/features/finance-analysis/financeAnalysisFormat';
import { serverNow } from '@etiquetachef/ui-shared/online';

const CURRENCIES = ['BRL', 'EUR', 'USD'] as const;
const DAY = 86_400_000;

/** Default: últimos ~6 meses até hoje. */
function defaultFrom(): string {
  return msToDateInput(serverNow() - 182 * DAY);
}

/**
 * Tela `/finance/cashflow`: fluxo de caixa do razão interno por período
 * (dia/semana/mês), por moeda, com acumulado e quebra por tag.
 *
 * Origem: #76 — 2026-06-21 23:20 +0200 (Financeiro — telas Visão geral,
 * Caixa e Balanço).
 *
 * Contrato: callable `computeCashflow` (leitura, shim `requireSuperAdmin` —
 * qualquer papel admin, `docs/permissions.md`) com `dateFrom`/`dateTo`
 * (meio-dia UTC dos dias; default últimos 182 dias), `granularity` (DAY |
 * WEEK | MONTH, default MONTH) e filtros opcionais `accountId`, `currency`,
 * `tags`. `listFinanceTags` alimenta o filtro de tags (falha só loga).
 * Resposta atrasada é descartada (`alive`); falha → `Alert`. Sem escrita.
 *
 * Usado por: `routes/router.tsx` (rota `finance/cashflow`, dentro do
 * `RequireSuperAdmin`), menu Financeiro → "Caixa" (`AdminLayout`). Só
 * `apps/admin` (web). Spec: `docs/screens/finance-cashflow-admin.md`.
 *
 * Arquitetura: agregação no servidor (Admin SDK sobre `finance_entries`);
 * cortes por dia no fuso da conta (#105). Um bloco por moeda, sem câmbio.
 *
 * Dados: `ComputeCashflowResult` (`packages/types/src/finance.ts`) —
 * `buckets[]` só com períodos que têm lançamento (`periodKey` `AAAA-MM-DD`
 * para dia/semana, `AAAA-MM` para mês), `totalsByCurrency` com
 * `inCents`/`outCents`/`netCents`, `accumulatedByCurrency` e
 * `openingByCurrency` — tudo em CENTAVOS; `capped` se bateu o teto.
 *
 * Dicas: `dateFrom`/`dateTo` inválidos (input vazio) não disparam a
 * callable — a tela mantém o último resultado.
 *
 * Issues: #76 (origem), #105 (corte por fuso).
 *
 * Histórico:
 *  - 2026-10-10 14:53 +0200 — (#798) cabeçalho padrão (#796).
 */
export function FinanceCashflowPage(): React.JSX.Element {
  const { t } = useTranslation();
  const [accounts, setAccounts] = useState<FinanceAccount[]>([]);
  const [knownTags, setKnownTags] = useState<string[]>([]);
  const [dateFrom, setDateFrom] = useState(defaultFrom());
  const [dateTo, setDateTo] = useState(msToDateInput(serverNow()));
  const [granularity, setGranularity] = useState<FinanceCashflowGranularity>('MONTH');
  const [accountId, setAccountId] = useState('');
  const [currency, setCurrency] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [result, setResult] = useState<ComputeCashflowResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const unsub = subscribeFinanceAccounts((r) => {
      if (r.kind === 'READY') setAccounts(r.accounts);
    });
    listFinanceTagsCallable({})
      .then((r) => {
        setKnownTags(r.tags);
      })
      .catch((err: unknown) => {
        console.error('listFinanceTags failed', err);
      });
    return (): void => {
      unsub();
    };
  }, []);

  useEffect(() => {
    // Meio-dia UTC dos dias escolhidos: cortes por dia no fuso da conta (#105).
    const from = dayNoonUtcMs(dateFrom);
    const to = dayNoonUtcMs(dateTo);
    if (from === undefined || to === undefined) return;
    let alive = true;
    setLoading(true);
    setError(false);
    computeCashflowCallable({
      dateFrom: from,
      dateTo: to,
      granularity,
      ...(accountId !== '' ? { accountId } : {}),
      ...(currency !== '' ? { currency: currency as Currency } : {}),
      ...(tags.length > 0 ? { tags } : {}),
    })
      .then((r) => {
        if (alive) setResult(r);
      })
      .catch((err: unknown) => {
        console.error('computeCashflow failed', err);
        if (alive) setError(true);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return (): void => {
      alive = false;
    };
  }, [dateFrom, dateTo, granularity, accountId, currency, tags]);

  const currenciesInResult = useMemo(() => {
    const set = new Set<string>();
    for (const b of result?.buckets ?? []) {
      for (const c of Object.keys(b.totalsByCurrency)) set.add(c);
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [result]);

  const accountOptions: SelectProps.Option[] = [
    { value: '', label: t('admin.finance.analysis.filters.allAccounts') },
    ...accounts.map((a) => ({ value: a.id, label: a.name })),
  ];
  const currencyOptions: SelectProps.Option[] = [
    { value: '', label: t('admin.finance.analysis.filters.allCurrencies') },
    ...CURRENCIES.map((c) => ({ value: c, label: c })),
  ];
  const granularityOptions: SelectProps.Option[] = ALL_FINANCE_CASHFLOW_GRANULARITIES.map((g) => ({
    value: g,
    label: t(`admin.finance.cashflow.granularity.${g}`),
  }));
  const tagOptions: MultiselectProps.Option[] = knownTags.map((tg) => ({ value: tg, label: tg }));

  return (
    <ContentLayout
      header={
        <Header variant="h1" description={t('admin.finance.cashflow.subtitle')}>
          {t('admin.finance.cashflow.title')}
        </Header>
      }
    >
      <SpaceBetween size="l">
        <Container>
          <SpaceBetween size="xs" direction="horizontal">
            <DatePicker
              value={dateFrom}
              onChange={({ detail }): void => {
                setDateFrom(detail.value);
              }}
              placeholder={t('admin.finance.cashflow.from')}
            />
            <DatePicker
              value={dateTo}
              onChange={({ detail }): void => {
                setDateTo(detail.value);
              }}
              placeholder={t('admin.finance.cashflow.to')}
            />
            <Select
              selectedOption={granularityOptions.find((o) => o.value === granularity) ?? null}
              options={granularityOptions}
              onChange={({ detail }): void => {
                setGranularity(detail.selectedOption.value as FinanceCashflowGranularity);
              }}
            />
            <Select
              selectedOption={accountOptions.find((o) => o.value === accountId) ?? null}
              options={accountOptions}
              onChange={({ detail }): void => {
                setAccountId(detail.selectedOption.value ?? '');
              }}
            />
            <Select
              selectedOption={currencyOptions.find((o) => o.value === currency) ?? null}
              options={currencyOptions}
              onChange={({ detail }): void => {
                setCurrency(detail.selectedOption.value ?? '');
              }}
            />
            <Multiselect
              selectedOptions={tags.map((tg) => ({ value: tg, label: tg }))}
              options={tagOptions}
              placeholder={t('admin.finance.cashflow.tagsFilter')}
              onChange={({ detail }): void => {
                setTags(detail.selectedOptions.map((o) => o.value ?? '').filter((v) => v !== ''));
              }}
            />
          </SpaceBetween>
        </Container>

        {error && <Alert type="error">{t('admin.finance.analysis.errorLoad')}</Alert>}
        {result?.capped === true && (
          <Alert type="warning">{t('admin.finance.analysis.capped')}</Alert>
        )}
        {!loading && (result?.buckets.length ?? 0) === 0 && !error && (
          <Alert type="info">{t('admin.finance.analysis.empty')}</Alert>
        )}

        {currenciesInResult.map((c) => (
          <CashflowCurrencyBlock
            key={c}
            currency={c as Currency}
            buckets={result?.buckets ?? []}
          />
        ))}

        <TagBreakdownTable
          items={result?.byTag ?? []}
          title={t('admin.finance.cashflow.byTag')}
        />
      </SpaceBetween>
    </ContentLayout>
  );
}

/** Bloco por moeda: gráfico entradas × saídas + tabela com saldo acumulado. */
function CashflowCurrencyBlock({
  currency,
  buckets,
}: {
  currency: Currency;
  buckets: FinanceCashflowBucket[];
}): React.JSX.Element {
  const { t } = useTranslation();
  const rows = buckets.filter((b) => b.totalsByCurrency[currency] !== undefined);
  const fmt = (v: number): string => formatCurrencyCents(v, currency);
  const inData = rows.map((b) => ({ x: b.periodKey, y: b.totalsByCurrency[currency]?.inCents ?? 0 }));
  const outData = rows.map((b) => ({
    x: b.periodKey,
    y: b.totalsByCurrency[currency]?.outCents ?? 0,
  }));

  return (
    <Container header={<Header variant="h3">{currency}</Header>}>
      <SpaceBetween size="m">
        <BarChart
          xScaleType="categorical"
          height={240}
          hideFilter
          ariaLabel={t('admin.finance.cashflow.chartAria')}
          i18nStrings={{ yTickFormatter: fmt }}
          series={[
            { title: t('admin.finance.analysis.columns.in'), type: 'bar', data: inData },
            { title: t('admin.finance.analysis.columns.out'), type: 'bar', data: outData },
          ]}
          empty={
            <Box textAlign="center" color="inherit">
              {t('admin.finance.analysis.empty')}
            </Box>
          }
        />
        <Table<FinanceCashflowBucket>
          items={rows}
          trackBy="periodKey"
          variant="embedded"
          columnDefinitions={[
            {
              id: 'period',
              header: t('admin.finance.cashflow.columns.period'),
              cell: (b): React.ReactNode => (
                <SpaceBetween size="xs" direction="horizontal">
                  <span>{b.periodKey}</span>
                  {b.isForecast && <Badge color="blue">{t('admin.finance.cashflow.forecast')}</Badge>}
                </SpaceBetween>
              ),
            },
            {
              id: 'in',
              header: t('admin.finance.analysis.columns.in'),
              cell: (b): React.ReactNode => (
                <Box textAlign="right">{fmt(b.totalsByCurrency[currency]?.inCents ?? 0)}</Box>
              ),
            },
            {
              id: 'out',
              header: t('admin.finance.analysis.columns.out'),
              cell: (b): React.ReactNode => (
                <Box textAlign="right">{fmt(b.totalsByCurrency[currency]?.outCents ?? 0)}</Box>
              ),
            },
            {
              id: 'net',
              header: t('admin.finance.analysis.columns.net'),
              cell: (b): React.ReactNode => (
                <SignedCents cents={b.totalsByCurrency[currency]?.netCents ?? 0} currency={currency} />
              ),
            },
            {
              id: 'accumulated',
              header: t('admin.finance.cashflow.columns.accumulated'),
              cell: (b): React.ReactNode => (
                <SignedCents cents={b.accumulatedByCurrency[currency] ?? 0} currency={currency} />
              ),
            },
          ]}
        />
      </SpaceBetween>
    </Container>
  );
}
